import { API_TYPES } from "@src/appV2/api";
import { z } from "zod";

export const tierTypeSchema = z.enum(["GOLD", "SILVER", "LIMITED"]);

export type TierType = z.infer<typeof tierTypeSchema>;

const eventTypesSchema = z.enum([
  "SHIFT_COMPLETION",
  "SHIFT_ARRIVAL",
  "SHIFT_CANCELLATION",
  "SHIFT_NO_SHOW",
  "SHIFT_ABANDONMENT",
]);

const eventToDropOffSchema = z.enum(["SUCCESS", "FAILURE", "NO_NEXT_DROP_OFF"]);

const eventCountSchema = z.object({
  dropOff: eventToDropOffSchema,
  successes: z.number().min(0),
  totalEvents: z.number().min(0),
});

const baseWorkerEventSchema = z.object({
  eventType: eventTypesSchema,
  meta: z.object({}),
  occurredAt: z.string().datetime(),
});

const shiftArrivalEventSchema = baseWorkerEventSchema.extend({
  eventType: z.literal("SHIFT_ARRIVAL"),
  meta: z.object({
    clockInCreatedAt: z.string().datetime(),
    start: z.string().datetime(),
    actualArrivalTime: z.string().datetime().optional(),
    bookingLeadTimeInMinutes: z.number(),
    isAfterAnotherShift: z.boolean(),
    manualOverride: z.boolean().optional(),
  }),
});
const shiftCompletionEventSchema = baseWorkerEventSchema.extend({
  eventType: z.literal("SHIFT_COMPLETION"),
});

const shiftCancellationEventSchema = baseWorkerEventSchema.extend({
  eventType: z.literal("SHIFT_CANCELLATION"),
  meta: z.object({ leadTimeInHours: z.number() }),
});

const shiftNoShowEventSchema = baseWorkerEventSchema.extend({
  eventType: z.literal("SHIFT_NO_SHOW"),
});

const shiftAbandonmentEventSchema = baseWorkerEventSchema.extend({
  eventType: z.literal("SHIFT_ABANDONMENT"),
});

const shiftRelationshipSchema = z.object({
  data: z.object({
    type: z.literal(API_TYPES.shift),
    id: z.string(),
  }),
});

const workerEventSchema = z.discriminatedUnion("eventType", [
  shiftArrivalEventSchema,
  shiftCompletionEventSchema,
  shiftCancellationEventSchema,
  shiftNoShowEventSchema,
  shiftAbandonmentEventSchema,
]);

export const getWorkerEventsQuerySchema = z
  .object({
    filter: z
      .object({ "shift.id": z.string().optional(), eventType: eventTypesSchema.optional() })
      .optional(),
  })
  .optional();

const workerEventDtoSchema = z.object({
  type: z.literal(API_TYPES.workerEvent),
  id: z.string(),
  attributes: workerEventSchema,
  relationships: z.object({ shift: shiftRelationshipSchema }),
});

export const getWorkerEventsResponseSchema = z.object({ data: z.array(workerEventDtoSchema) });

const tierDataSchema = z.object({
  type: tierTypeSchema,
  criteria: z.object({
    onTimeArrivals: eventCountSchema,
    shiftCompletionsWithoutCancel: eventCountSchema,
    lateCancels: z.object({
      dropOff: eventToDropOffSchema,
      recentLateCancels: z.number().min(0),
      totalEvents: z.number().min(0),
    }),
    recentShiftsWorked: z.number().min(0),
    limitedShiftsRemaining: z.number().min(0),
    recentShiftNoShows: z.number().min(0),
    recentShiftAbandonments: z.number().min(0),
  }),
  limitedEvent: workerEventSchema.optional(),
  tierLastChangedAt: z.string().datetime(),
});

const workerRelationshipSchema = z.object({
  data: z.object({
    type: z.literal(API_TYPES.worker),
    id: z.string(),
  }),
});

export const getTiersQuerySchema = z.object({
  filter: z.object({
    "worker.id": z.string().transform((value) => value.split(",")),
  }),
});

const tierSchema = z.object({
  type: z.literal(API_TYPES.tier),
  id: z.string(),
  attributes: tierDataSchema,
  relationships: z.object({ worker: workerRelationshipSchema }),
});

export const getTiersResponseSchema = z.object({ data: z.array(tierSchema) });

export type GetTiersResponse = z.infer<typeof getTiersResponseSchema>;

export type Tier = z.infer<typeof tierSchema>;
export type TierEventType = z.infer<typeof eventTypesSchema>;
export type WorkerEvent = z.infer<typeof workerEventSchema>;
export type ShiftArrivalEvent = z.infer<typeof shiftArrivalEventSchema>;
export type WorkerEventType = z.infer<typeof eventTypesSchema>;
export type GetWorkerEventsResponse = z.infer<typeof getWorkerEventsResponseSchema>;
